import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LegalModal from './LegalModal';
import useLang from '../hooks/useLang';
import { APP_VERSION } from '../version';

const translations = {
  es: {
    inicio: 'Inicio',
    sectores: 'Sectores',
    sectorPublico: 'Administración y Sector Público',
    sectorPublicoDesc: 'Certificación ENS y cumplimiento RD 311/2022',
    sectorDerechoPublico: 'Corporaciones de Derecho Público',
    sectorDerechoPublicoDesc: 'Colegios profesionales y consejos autonómicos',
    sectorPrivado: 'Sector Privado y Cadena de Suministro',
    sectorPrivadoDesc: 'NIS2, RGPD y gobernanza contractual',
    metodologia: 'Productos',
    prodSecureReceipt: 'SecureReceipt SaaS',
    prodSecureReceiptDesc: 'Digitalización certificada por IA y Veri*factu',
    prodAdcaTemporal: 'ADCA Temporal SaaS',
    prodAdcaTemporalDesc: 'Control de presencia y jornada criptográfica',
    prodSelfHealing: 'Self-Healing SOC',
    prodSelfHealingDesc: 'Ciberseguridad y auto-recuperación activa',
    prodWhistleblower: 'Canal Denuncias Cripto',
    prodWhistleblowerDesc: 'Canal de denuncias seguro E2E (Ley 2/2023)',
    prodLegalGen: 'Generador Legal IA',
    prodLegalGenDesc: 'Automatización de textos legales obligatorios',
    prodSaaSWidget: 'Widget Accesibilidad, Cookies e IA',
    prodSaaSWidgetDesc: 'Consentimiento RGPD, accesibilidad 2026 y atención por IA',
    prodAcoso: 'Protocolos de Acoso Laboral',
    prodAcosoDesc: 'Plan de igualdad y prevención del acoso por ley',
    casosExito: 'Casos Exitosos',
    sobreMi: 'Auditores',

    tarifas: 'Tarifas',

    contacto: 'Contacto',
    auditorIa: 'Hablar con Auditor IA',
    auditorIaConectando: 'Conectando...',
    auditorIaEnLinea: 'Colgar Auditor IA',
    desistimiento: 'Desistimiento UE',
    diagnostico: 'Diagnóstico Express',
    closeMenu: 'Cerrar menú de navegación',
    openMenu: 'Abrir menú de navegación',
    escanerExpress: 'Escáner Express',
    generadorLegal: 'Generador Legal IA',
    adcaTemporal: 'ADCA Temporal',
    selfHealing: 'Self-Healing',
    guiaFacil: 'Guía fácil',
    testEslabondebil: 'Test Eslabón Débil',
    prodEslabondebil: 'Test Eslabón Débil NIS2',
    prodEslabondebilDesc: 'Simulador OSINT interactivo de proveedores y ciberhigiene',
    prodLaboral: 'Derecho Laboral Digital',
    prodLaboralDesc: 'Registro de jornada, teletrabajo y desconexión digital (Art. 88 LOPDGDD)',
    prodMorosos: 'Defensa frente a Morosidad',
    prodMorososDesc: 'Baja express en ASNEF/EXPERIAN y daños al honor',
    prodIso: 'Implantación ISO 27001',
    prodIsoDesc: 'Roadmap y auditoría interna para certificación del SGSI',
    prodDenuncias: 'Canal Denuncias Ley 2/2023',
    prodDenunciasDesc: 'Portal anónimo independiente y protocolo formal de denuncias',
    prodCra: 'Ley de Ciberresiliencia CRA',
    prodCraDesc: 'Seguridad por diseño y marcado CE de software en la Unión Europea',
    prodEni: 'Esquema de Interoperabilidad (ENI)',
    prodEniDesc: 'Implantación de NTI e intercambio de datos con el Sector Público',
    prodNicho: 'Auditorías TISAX & PCI DSS',
    prodNichoDesc: 'Homologación y seguridad para automoción y medios de pago'
  },
  en: {

    inicio: 'Home',
    sectores: 'Sectors',
    sectorPublico: 'Public Administration & Public Sector',
    sectorPublicoDesc: 'ENS Certification & RD 311/2022 Compliance',
    sectorDerechoPublico: 'Public Law Corporations',
    sectorDerechoPublicoDesc: 'Professional associations & regional councils',
    sectorPrivado: 'Private Sector & Supply Chain',
    sectorPrivadoDesc: 'NIS2, GDPR & contractual governance',
    metodologia: 'Products',
    prodSecureReceipt: 'SecureReceipt SaaS',
    prodSecureReceiptDesc: 'Certified AI digitization & Veri*factu',
    prodAdcaTemporal: 'ADCA Temporal SaaS',
    prodAdcaTemporalDesc: 'Presence control & cryptographic work logs',
    prodSelfHealing: 'Self-Healing SOC',
    prodSelfHealingDesc: 'Cybersecurity & active self-healing',
    prodWhistleblower: 'Crypto Whistleblowing',
    prodWhistleblowerDesc: 'Secure E2E whistleblowing (Law 2/2023)',
    prodLegalGen: 'AI Legal Generator',
    prodLegalGenDesc: 'Automation of mandatory legal texts',
    prodSaaSWidget: 'Accessibility, Cookie & AI Widget',
    prodSaaSWidgetDesc: 'GDPR consent, 2026 accessibility & conversational AI helper',
    prodAcoso: 'Workplace Harassment Protocols',
    prodAcosoDesc: 'Equality plans and prevention protocols under legal standards',
    casosExito: 'Success Cases',
    sobreMi: 'Auditors',
    tarifas: 'Rates',

    contacto: 'Contact',
    auditorIa: 'Talk to AI Auditor',
    auditorIaConectando: 'Connecting...',
    auditorIaEnLinea: 'Hang up AI Auditor',
    desistimiento: 'EU Withdrawal',
    diagnostico: 'Express Assessment',
    closeMenu: 'Close navigation menu',
    openMenu: 'Open navigation menu',
    escanerExpress: 'Express Scanner',
    generadorLegal: 'AI Legal Generator',
    adcaTemporal: 'ADCA Temporal',
    selfHealing: 'Self-Healing',
    guiaFacil: 'Easy Guide',
    testEslabondebil: 'Weak Link Test',
    prodEslabondebil: 'Weak Link Test NIS2',
    prodEslabondebilDesc: 'Interactive OSINT vendor & cyber-hygiene simulator',
    prodLaboral: 'Digital Labor Law',
    prodLaboralDesc: 'Mandatory time-tracking, teleworking & off-hours disconnection',
    prodMorosos: 'Solvency Blacklist Defense',
    prodMorososDesc: 'ASNEF/Credit blacklist express removal & honor defense',
    prodIso: 'ISO 27001 ISMS Setup',
    prodIsoDesc: 'Roadmap and internal audit for ISMS certification',
    prodDenuncias: 'Whistleblowing Channel Law 2/2023',
    prodDenunciasDesc: 'Independent anonymous portal and formal reporting protocol',
    prodCra: 'Cyber Resilience Act (CRA)',
    prodCraDesc: 'Secure by design and CE software marking in the European Union',
    prodEni: 'Interoperability Scheme (ENI)',
    prodEniDesc: 'NTI implementation & data exchange with the Public Sector',
    prodNicho: 'TISAX & PCI DSS Audits',
    prodNichoDesc: 'Compliance and security for automotive and payment gateways'
  }
};


export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSectoresOpen, setIsSectoresOpen] = useState(false);
  const [isMobileSectoresOpen, setIsMobileSectoresOpen] = useState(false);
  const [isProductosOpen, setIsProductosOpen] = useState(false);
  const [isMobileProductosOpen, setIsMobileProductosOpen] = useState(false);
  const [isDesistimientoOpen, setIsDesistimientoOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';



  const sectoresTimeoutRef = useRef(null);
  const productosTimeoutRef = useRef(null);

  const handleSectoresEnter = () => {
    if (sectoresTimeoutRef.current) {
      clearTimeout(sectoresTimeoutRef.current);
      sectoresTimeoutRef.current = null;
    }
    setIsSectoresOpen(true);
  };

  const handleSectoresLeave = () => {
    sectoresTimeoutRef.current = setTimeout(() => {
      setIsSectoresOpen(false);
    }, 300); // 300ms de tolerancia
  };

  const handleProductosEnter = () => {
    if (productosTimeoutRef.current) {
      clearTimeout(productosTimeoutRef.current);
      productosTimeoutRef.current = null;
    }
    setIsProductosOpen(true);
  };

  const handleProductosLeave = () => {
    productosTimeoutRef.current = setTimeout(() => {
      setIsProductosOpen(false);
    }, 300); // 300ms de tolerancia
  };

  // Limpieza al desmontar
  useEffect(() => {
    return () => {
      if (sectoresTimeoutRef.current) {
        clearTimeout(sectoresTimeoutRef.current);
      }
      if (productosTimeoutRef.current) {
        clearTimeout(productosTimeoutRef.current);
      }
    };
  }, []);

  const lang = useLang();
  const t = translations[lang];

  const handleLanguageChange = (newLang) => {
    localStorage.setItem('lang', newLang);
    window.dispatchEvent(new CustomEvent('languageChange', { detail: newLang }));
  };


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    } else {
      document.body.style.overflow = '';
      document.body.style.height = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, [isMobileMenuOpen]);

  const closeMobile = () => {
    setIsMobileMenuOpen(false);
    setIsSectoresOpen(false);
    setIsMobileSectoresOpen(false);
  };

  const scrollToSection = (id) => {
    if (isHomePage) {
      if (id === 'hero') {
        navigate('/', { replace: true });
      }
      
      let attempts = 0;
      const maxAttempts = 30; // 30 * 50ms = 1500ms
      
      const tryScroll = () => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setIsMobileMenuOpen(false);
          setIsSectoresOpen(false);
          return true;
        }
        return false;
      };

      // Intentar de inmediato
      if (tryScroll()) return;

      // Intentar con polling si no se encuentra de inmediato (por ejemplo, lazy-loading)
      const interval = setInterval(() => {
        attempts++;
        const success = tryScroll();
        if (success || attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 50);
    } else {
      // Usar navigate para registrar el hash y disparar la escucha en App.js de forma interactiva
      navigate(`/#${id}`);
      setIsMobileMenuOpen(false);
      setIsSectoresOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 no-transform-transition transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-md border-b border-white/20 shadow-[0_8px_32px_rgba(255,255,255,0.85)]'
          : 'bg-white shadow-sm border-b border-slate-100'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo with New Badge Image */}
          <Link
            to="/"
            onClick={(e) => {
              if (isHomePage) {
                e.preventDefault();
                scrollToSection('hero');
              }
            }}
            className="flex items-center space-x-2 sm:space-x-3 group flex-shrink-0 cursor-pointer"
          >
            <img
              src="/logo-badge.png"
              alt="Auditorialegal - LegalSec Consulting"
              width={48}
              height={48}
              fetchPriority="high"
              decoding="async"
              className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover flex-shrink-0"
            />
            <div className="block">
              <div className="flex items-center gap-1.5">
                <span className="text-base sm:text-xl font-serif font-bold text-primary block">
                  Auditorialegal
                </span>
                <span className="bg-slate-100 border border-slate-200/60 text-slate-700 font-mono text-[8px] font-semibold px-1 rounded-sm tracking-wider uppercase leading-none py-0.5" title="Versión compilada">
                  {APP_VERSION}
                </span>
              </div>
              <span className="text-[9px] sm:text-[10px] text-muted-foreground block leading-tight">
                Auditoría | Cumplimiento | ENS
              </span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center lg:space-x-0.5 xl:space-x-1.5 flex-grow justify-center max-w-3xl mx-auto">
            {/* Sectores Dropdown */}
            <div
              className="relative"
              onMouseEnter={handleSectoresEnter}
              onMouseLeave={handleSectoresLeave}
            >
              <button
                className="px-2.5 lg:px-2 xl:px-3.5 py-2 text-[9.5px] lg:text-[8px] xl:text-[10px] font-bold tracking-wide text-slate-800 hover:text-accent no-transform-transition rounded-md hover:bg-slate-100 flex items-center gap-0.5"
              >
                {t.sectores}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              
              {isSectoresOpen && (
                <div className="absolute top-full left-0 mt-1 w-80 bg-white rounded-lg shadow-strong border border-border py-2 animate-fade-in">
                  <Link
                    to="/sector-publico"
                    onClick={() => setIsSectoresOpen(false)}
                    className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                  >
                    <div className="font-semibold">{t.sectorPublico}</div>
                    <div className="text-xs text-muted-foreground">{t.sectorPublicoDesc}</div>
                  </Link>
                  <Link
                    to="/sector-derecho-publico"
                    onClick={() => setIsSectoresOpen(false)}
                    className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                  >
                    <div className="font-semibold">{t.sectorDerechoPublico}</div>
                    <div className="text-xs text-muted-foreground">{t.sectorDerechoPublicoDesc}</div>
                  </Link>
                  <Link
                    to="/sector-privado"
                    onClick={() => setIsSectoresOpen(false)}
                    className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                  >
                    <div className="font-semibold">{t.sectorPrivado}</div>
                    <div className="text-xs text-muted-foreground">{t.sectorPrivadoDesc}</div>
                  </Link>
                </div>
              )}
            </div>

            {/* Productos Dropdown */}
            <div
              className="relative"
              onMouseEnter={handleProductosEnter}
              onMouseLeave={handleProductosLeave}
            >
              <button
                className="px-2.5 lg:px-2 xl:px-3.5 py-2 text-[9.5px] lg:text-[8px] xl:text-[10px] font-bold tracking-wide text-slate-800 hover:text-accent no-transform-transition rounded-md hover:bg-slate-100 flex items-center gap-0.5"
              >
                {t.metodologia}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {isProductosOpen && (
                <div className="absolute top-full left-0 mt-1 w-80 bg-white rounded-lg shadow-strong border border-border py-2 animate-fade-in text-left">
                  <Link
                    to="/productos"
                    onClick={() => setIsProductosOpen(false)}
                    className="block w-full px-4 py-2 border-b border-slate-100/50 text-left text-[9px] font-bold text-slate-400 tracking-wider uppercase hover:text-slate-600 font-mono no-transform-transition"
                  >
                    {lang === 'es' ? 'Ver Catálogo Completo' : 'View Full Catalog'}
                  </Link>
                  <div className="max-h-[240px] overflow-y-auto pr-0.5 custom-scrollbar divide-y divide-slate-100/30">
                    <Link
                      to="/secure-receipt"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodSecureReceipt}
                        <span className="bg-cyan-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          Veri*factu
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodSecureReceiptDesc}</div>
                    </Link>
                    <Link
                      to="/adca-temporal"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodAdcaTemporal}
                        <span className="bg-amber-500 text-slate-950 text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider animate-pulse font-mono">
                          SaaS
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodAdcaTemporalDesc}</div>
                    </Link>
                    <Link
                      to="/self-healing"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodSelfHealing}
                        <span className="bg-emerald-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          SOC
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodSelfHealingDesc}</div>
                    </Link>
                    <Link
                      to="/simulador-eslabon-debil"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none text-emerald-600">
                        {t.prodEslabondebil}
                        <span className="bg-emerald-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono animate-pulse">
                          OSINT
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodEslabondebilDesc}</div>
                    </Link>
                    <Link
                      to="/canal-denuncias"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodWhistleblower}
                        <span className="bg-purple-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          Ley 2/2023
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodWhistleblowerDesc}</div>
                    </Link>
                    <Link
                      to="/generador-legal"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodLegalGen}
                        <span className="bg-blue-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          IA
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodLegalGenDesc}</div>
                    </Link>
                    <Link
                      to="/tarifas"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodSaaSWidget}
                        <span className="bg-amber-500 text-slate-950 text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          Widget
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodSaaSWidgetDesc}</div>
                    </Link>
                    <Link
                      to="/tarifas"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition font-medium"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodAcoso}
                        <span className="bg-red-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          Ley
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodAcosoDesc}</div>
                    </Link>
                    <Link
                      to="/productos"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodLaboral}
                        <span className="bg-indigo-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          Trabajo
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodLaboralDesc}</div>
                    </Link>
                    <Link
                      to="/productos"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodMorosos}
                        <span className="bg-rose-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          Honor
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodMorososDesc}</div>
                    </Link>
                    <Link
                      to="/productos"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodIso}
                        <span className="bg-cyan-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          SGSI
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodIsoDesc}</div>
                    </Link>
                    <Link
                      to="/productos"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodDenuncias}
                        <span className="bg-fuchsia-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          Denuncias
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodDenunciasDesc}</div>
                    </Link>
                    <Link
                      to="/productos"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodCra}
                        <span className="bg-fuchsia-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          CRA
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodCraDesc}</div>
                    </Link>
                    <Link
                      to="/productos"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodEni}
                        <span className="bg-blue-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          ENI
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodEniDesc}</div>
                    </Link>
                    <Link
                      to="/productos"
                      onClick={() => setIsProductosOpen(false)}
                      className="block w-full px-4 py-3 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent no-transform-transition"
                    >
                      <div className="font-semibold flex items-center gap-1.5 leading-none">
                        {t.prodNicho}
                        <span className="bg-fuchsia-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase leading-none tracking-wider font-mono">
                          PCI
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{t.prodNichoDesc}</div>
                    </Link>
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/casos-exito"
              className="px-2.5 lg:px-2 xl:px-3.5 py-2 text-[9.5px] lg:text-[8px] xl:text-[10px] font-bold tracking-wide text-slate-800 hover:text-accent no-transform-transition rounded-md hover:bg-slate-100 block"
            >
              {t.casosExito}
            </Link>
            <Link
              to="/tarifas"
              onClick={closeMobile}
              className="px-2.5 lg:px-2 xl:px-3.5 py-2 text-[9.5px] lg:text-[8px] xl:text-[10px] font-bold tracking-wide text-slate-800 hover:text-accent no-transform-transition rounded-md hover:bg-slate-100"
            >
              {t.tarifas}
            </Link>
            <Link
              to="/sobre-mi"
              onClick={closeMobile}
              className="px-2.5 lg:px-2 xl:px-3.5 py-2 text-[9.5px] lg:text-[8px] xl:text-[10px] font-bold tracking-wide text-slate-800 hover:text-accent no-transform-transition rounded-md hover:bg-slate-100"
            >
              {t.sobreMi}
            </Link>
            <Link
              to="/guia-facil"
              className="px-2.5 lg:px-2 xl:px-3.5 py-2 text-[9.5px] lg:text-[8px] xl:text-[10px] font-bold tracking-wide text-amber-600 hover:text-accent no-transform-transition rounded-md hover:bg-amber-500/5 flex items-center gap-0.5 border border-amber-500/25 animate-pulse"
            >
              ✨ {t.guiaFacil}
            </Link>

            <button
              onClick={() => scrollToSection('contacto')}
              className="px-2.5 lg:px-2 xl:px-3.5 py-2 text-[9.5px] lg:text-[8px] xl:text-[10px] font-bold tracking-wide text-slate-800 hover:text-accent no-transform-transition rounded-md hover:bg-slate-100"
            >
              {t.contacto}
            </button>
          </div>



          {/* Sticky CTA Button with EU Alert */}
          <div className="hidden lg:flex items-center gap-1 xl:gap-1.5 flex-shrink-0">
            <button
              onClick={() => setIsDesistimientoOpen(true)}
              className="flex items-center gap-1 px-1 lg:px-1 xl:px-1.5 2xl:px-2 py-1.5 bg-destructive/10 hover:bg-destructive/20 border border-destructive/30 rounded-md text-[9px] lg:text-[9px] xl:text-[9.5px] 2xl:text-xs font-semibold text-destructive transition-colors relative whitespace-nowrap"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-destructive"></span>
              </span>
              <span className="hidden 2xl:inline">{t.desistimiento}</span>
              <span className="inline 2xl:hidden">{lang === 'es' ? 'Desistimiento' : 'Withdrawal'}</span>
            </button>



            {/* Desktop Language Switcher - Single Small Flag Toggle */}
            <button
              onClick={() => handleLanguageChange(lang === 'es' ? 'en' : 'es')}
              className="transition-all duration-200 hover:scale-110 active:scale-95 px-1.5 py-1 rounded-md bg-muted/60 border border-border/80 flex items-center gap-0.5 font-sans font-bold text-xs text-primary flex-shrink-0"
              aria-label={lang === 'es' ? "ES - Cambiar idioma" : "EN - Switch language"}
            >
              {lang === 'es' ? (
                <>
                  <img src="https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/4x3/es.svg" alt="Español" className="w-3.5 h-2.5 object-cover rounded-sm shadow-sm" />
                  <span>ES</span>
                </>
              ) : (
                <>
                  <img src="https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/4x3/gb.svg" alt="English" className="w-3.5 h-2.5 object-cover rounded-sm shadow-sm" />
                  <span>EN</span>
                </>
              )}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-primary hover:bg-muted rounded-md"
            aria-label={isMobileMenuOpen ? t.closeMenu : t.openMenu}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div id="mobile-menu" className="lg:hidden py-4 border-t border-border max-h-[calc(100dvh-5rem)] overflow-y-auto">
            <div className="flex flex-col space-y-2">
              <div className="px-4 pb-2 flex items-center gap-2">
                <button
                  onClick={() => {
                    setIsDesistimientoOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-destructive/10 hover:bg-destructive/20 border border-destructive/30 rounded-md text-xs font-bold text-destructive transition-colors relative"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive"></span>
                  </span>
                  {t.desistimiento}
                </button>

                {/* Mobile Language Switcher */}
                <button
                  onClick={() => handleLanguageChange(lang === 'es' ? 'en' : 'es')}
                  className="transition-all duration-200 active:scale-95 px-3 py-2.5 rounded-md bg-muted/60 border border-border/80 flex items-center justify-center gap-1.5 font-sans font-bold text-xs text-primary flex-shrink-0"
                  aria-label={lang === 'es' ? "ES - Cambiar idioma" : "EN - Switch language"}
                >
                  {lang === 'es' ? (
                    <>
                      <img src="https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/4x3/es.svg" alt="Español" className="w-4 h-3 object-cover rounded-sm shadow-sm" />
                      <span>ES</span>
                    </>
                  ) : (
                    <>
                      <img src="https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/4x3/gb.svg" alt="English" className="w-4 h-3 object-cover rounded-sm shadow-sm" />
                      <span>EN</span>
                    </>
                  )}
                </button>
              </div>
            <div>
              <button
                onClick={() => setIsMobileSectoresOpen(!isMobileSectoresOpen)}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition"
              >
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t.sectores}</span>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isMobileSectoresOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileSectoresOpen && (
                <div className="pl-4 border-l-2 border-accent/20 ml-4 mb-2 space-y-1 animate-fade-in">
                  <Link
                    to="/sector-publico"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition"
                  >
                    {t.sectorPublico}
                  </Link>
                  <Link
                    to="/sector-derecho-publico"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition"
                  >
                    {t.sectorDerechoPublico}
                  </Link>
                  <Link
                    to="/sector-privado"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition"
                  >
                    {t.sectorPrivado}
                  </Link>
                </div>
              )}
            </div>

            <div>
              <button
                onClick={() => setIsMobileProductosOpen(!isMobileProductosOpen)}
                className="w-full flex items-center justify-between px-4 py-3 text-left text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition"
              >
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t.metodologia}</span>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isMobileProductosOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileProductosOpen && (
                <div className="pl-4 border-l-2 border-accent/20 ml-4 mb-2 space-y-1 animate-fade-in text-left">
                  <Link
                    to="/productos"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-xs font-bold text-slate-400 tracking-wider uppercase hover:text-slate-600 rounded-md font-mono"
                  >
                    {lang === 'es' ? 'Ver Catálogo Completo' : 'View Full Catalog'}
                  </Link>
                  <Link
                    to="/secure-receipt"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold"
                  >
                    {t.prodSecureReceipt}
                  </Link>
                  <Link
                    to="/adca-temporal"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold"
                  >
                    {t.prodAdcaTemporal}
                  </Link>
                  <Link
                    to="/self-healing"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold"
                  >
                    {t.prodSelfHealing}
                  </Link>
                  <Link
                    to="/simulador-eslabon-debil"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-emerald-650 hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-bold"
                  >
                    ✨ {t.prodEslabondebil}
                  </Link>
                  <Link
                    to="/canal-denuncias"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold"
                  >
                    {t.prodWhistleblower}
                  </Link>
                  <Link
                    to="/generador-legal"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold border-t border-slate-100/30 pt-1"
                  >
                    {t.prodLegalGen}
                  </Link>
                  <Link
                    to="/tarifas"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold border-t border-slate-100/30 pt-1"
                  >
                    {t.prodSaaSWidget}
                  </Link>
                  <Link
                    to="/tarifas"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold border-t border-slate-100/30 pt-1"
                  >
                    {t.prodAcoso}
                  </Link>
                  <Link
                    to="/productos"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold border-t border-slate-100/30 pt-1"
                  >
                    {t.prodLaboral}
                  </Link>
                  <Link
                    to="/productos"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold border-t border-slate-100/30 pt-1"
                  >
                    {t.prodMorosos}
                  </Link>
                  <Link
                    to="/productos"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold border-t border-slate-100/30 pt-1"
                  >
                    {t.prodIso}
                  </Link>
                  <Link
                    to="/productos"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold border-t border-slate-100/30 pt-1"
                  >
                    {t.prodDenuncias}
                  </Link>
                  <Link
                    to="/productos"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold border-t border-slate-100/30 pt-1"
                  >
                    {t.prodCra}
                  </Link>
                  <Link
                    to="/productos"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold border-t border-slate-100/30 pt-1"
                  >
                    {t.prodEni}
                  </Link>
                  <Link
                    to="/productos"
                    onClick={closeMobile}
                    className="block pl-4 pr-4 py-2 text-left text-sm text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition font-semibold border-t border-slate-100/30 pt-1"
                  >
                    {t.prodNicho}
                  </Link>
                </div>
              )}
            </div>
              <Link
                to="/casos-exito"
                onClick={closeMobile}
                className="block px-4 py-3 text-left text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition"
              >
                {t.casosExito}
              </Link>
              <Link
                to="/tarifas"
                onClick={closeMobile}
                className="px-4 py-3 text-left text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition"
              >
                {t.tarifas}
              </Link>
              <Link
                to="/sobre-mi"
                onClick={closeMobile}
                className="px-4 py-3 text-left text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition"
              >
                {t.sobreMi}
              </Link>
              <Link
                to="/guia-facil"
                onClick={closeMobile}
                className="block px-4 py-3 text-left font-black text-amber-600 hover:bg-amber-500/5 rounded-md no-transform-transition flex items-center gap-1 border border-amber-500/15 animate-pulse"
              >
                ✨ {t.guiaFacil}
              </Link>



              <button
                onClick={() => scrollToSection('contacto')}
                className="px-4 py-3 text-left text-primary hover:bg-accent-subtle hover:text-accent rounded-md no-transform-transition"
              >
                {t.contacto}
              </button>
            </div>
          </div>
        )}
      </div>
      <LegalModal
        open={isDesistimientoOpen}
        type="desistimiento"
        onOpenChange={setIsDesistimientoOpen}
      />
    </nav>
  );
};

export default Navigation;
